import * as Yup from 'yup';
import { isNumber, isString } from 'lodash';
import type { TFunction } from 'react-i18next';
// Import Constants
import { l } from 'constants/common';
import { ResponseRegexTypeEnum, SMSProviderFieldsEnum, SMSTestSendFieldsEnum } from './constants';

const getIsPathOfKey = (path: string) => path[path.length - 2] === '0';
const getIsPathOfValue = (path: string) => path[path.length - 2] === '1';

const nestedSchema = (t: TFunction, inputType: 'text' | 'number') => {
    return Yup.lazy((value) => {
        if (Array.isArray(value)) {
            const schema = Yup.lazy((item) => {
                if (Array.isArray(item) && item.length === 2) {
                    return Yup.array().of(
                        Yup.mixed()
                            .default('')
                            .test('tuple Key', t(l.InvalidKey), (value, message) => {
                                if (getIsPathOfValue(message.path)) return true;

                                return typeof value === 'string';
                            })
                            .test('tuple Value', 'Invalid Value', (value, message) => {
                                if (getIsPathOfKey(message.path) || Array.isArray(value)) {
                                    return true;
                                }

                                if (inputType === 'text') {
                                    return isString(value);
                                }

                                return isNumber(value) || value === null;
                            })
                            .test('tuple Array Value', (value, message) => {
                                if (getIsPathOfKey(message.path) || !Array.isArray(value)) {
                                    return true;
                                }

                                return nestedSchema(t, inputType).validateSync(value, {
                                    ...message,
                                    recursive: true,
                                    abortEarly: false,
                                });
                            }),
                    );
                }

                return Yup.mixed().notRequired();
            });

            return Yup.array().of(schema);
        }

        return Yup.mixed().notRequired().default([]);
    });
};

const responseRegexPropertiesSchema = (t: TFunction) => {
    return Yup.object().shape({
        [SMSProviderFieldsEnum.ReplaceBy]: Yup.string().when(
            `$.${SMSProviderFieldsEnum.ResponseRegex}.${SMSProviderFieldsEnum.Type}`,
            {
                is: ResponseRegexTypeEnum.Other,
                then: Yup.string().required(t('CannotBeEmpty', { fieldName: t(l.ReplaceBy) })),
                otherwise: Yup.string(),
            },
        ),
        [SMSProviderFieldsEnum.ErrorCode]: Yup.string(),
        [SMSProviderFieldsEnum.ErrorMessage]: Yup.string(),
        [SMSProviderFieldsEnum.ExternalId]: Yup.string(),
        [SMSProviderFieldsEnum.DeliveryStatus]: Yup.string(),
    });
};

export const getSMSProviderCECValidationSchema = (t: TFunction) => {
    return Yup.object({
        [SMSProviderFieldsEnum.PartnerId]: Yup.number()
            .typeError(t('PleaseSelect', { fieldName: t(l.Partner) })) // if value is not a number
            .required(t('CannotBeEmpty', { fieldName: t(l.Partner) }))
            .nullable(true),

        [SMSProviderFieldsEnum.Url]: Yup.string().required(t('CannotBeEmpty', { fieldName: t(l.Url) })),

        [SMSProviderFieldsEnum.ApiKey]: Yup.string().nullable(true),

        [SMSProviderFieldsEnum.Name]: Yup.string().required(t('CannotBeEmpty', { fieldName: t(l.Name) })),

        [SMSProviderFieldsEnum.Description]: Yup.string().nullable(true),

        [SMSProviderFieldsEnum.UserName]: Yup.string().required(t('CannotBeEmpty', { fieldName: t(l.Username) })),

        [SMSProviderFieldsEnum.Password]: Yup.string().required(t('CannotBeEmpty', { fieldName: t(l.Password) })),

        [SMSProviderFieldsEnum.FromAddress]: Yup.string().nullable(true),

        [SMSProviderFieldsEnum.RequestType]: Yup.number()
            .required(t('CannotBeEmpty', { fieldName: t(l.RequestType) }))
            .nullable(true),

        [SMSProviderFieldsEnum.MediaType]: Yup.string().nullable(true),

        [SMSProviderFieldsEnum.PostMessageBody]: Yup.string().nullable(true),

        [SMSProviderFieldsEnum.Note]: Yup.string().nullable(true),

        [SMSProviderFieldsEnum.MaxDegreeOfParallelism]: Yup.number()
            .required(t('CannotBeEmpty', { fieldName: t(l.MaxDegreeOfParallelism) }))
            .min(1, t(l.MustBeBiggerOrEqualThan, { fieldName: t(l.MaxDegreeOfParallelism), count: 1 }))
            .max(10, t(l.MustBeSmallerOrEqualThan, { fieldName: t(l.MaxDegreeOfParallelism), count: 10 })),

        [SMSProviderFieldsEnum.DelayTimeout]: Yup.number()
            .required(t('CannotBeEmpty', { fieldName: t(l.DelayTimeout) }))
            .min(0, t(l.MustBeBiggerOrEqualThan, { fieldName: t(l.DelayTimeout), count: 0 }))
            .max(10000, t(l.MustBeSmallerOrEqualThan, { fieldName: t(l.DelayTimeout), count: 10000 })),

        [SMSProviderFieldsEnum.ResponseRegex]: Yup.object()
            .shape({
                [SMSProviderFieldsEnum.Type]: Yup.number()
                    .required(t('CannotBeEmpty', { fieldName: t(l.Type) }))
                    .nullable(true),
                [SMSProviderFieldsEnum.Properties]: responseRegexPropertiesSchema(t),
                [SMSProviderFieldsEnum.Error]: responseRegexPropertiesSchema(t),
                [SMSProviderFieldsEnum.SuccessCode]: Yup.number().nullable(true),
                [SMSProviderFieldsEnum.DefaultCode]: Yup.number().nullable(true),
                [SMSProviderFieldsEnum.CodeMap]: nestedSchema(t, 'number'),
            })
            .required(t('CannotBeEmpty', { fieldName: t(l.ResponseRegex) })),

        [SMSProviderFieldsEnum.AdditionalFields]: nestedSchema(t, 'text'),
    });
};

export const getSMSTestSendValidationSchema = (t: TFunction) => {
    return Yup.object({
        [SMSTestSendFieldsEnum.MobilePhone]: Yup.string().required(t(l.CannotBeEmpty, { fieldName: t(l.PhoneNumber) })),
    });
};
